// LO QUE SALTA A LA VISTA — el resumen de la pantalla, en frases cortas.
//
// Esto viene de `Stats/TabResumen.js` (componente `Insights`), la única parte de
// la pantalla vieja que la nueva no reusaba. Era lo que más gustaba: una lista
// de frases, sin gráfico, que se lee de corrido. Tenía ocho renglones.
//
// ── El error que este archivo corrige ────────────────────────────────────
//
// Al traerlo se dejaron tres renglones y se sacaron los otros cinco "porque los
// duplicaba un bloque de detalle". Eso era un error de criterio: un RESUMEN QUE
// APUNTA AL DETALLE NO ESTÁ DUPLICANDO NADA, está haciendo su trabajo. El que
// abre el Monitor treinta segundos se lleva las conclusiones de acá; el que
// quiere la prueba baja al bloque que la explica.
//
// El problema real de aquellos cinco renglones nunca fue repetirse: era que
// daban NÚMEROS DISTINTOS a los del bloque de detalle, porque cada uno se
// calculaba por su cuenta. "El domingo te deja $0" acá arriba —contando como
// cero los domingos que la playa no abre— y un gráfico dos pantallas más abajo
// que ni siquiera dibuja el domingo. Dos respuestas a la misma pregunta, y el
// dueño sin forma de saber cuál creer.
//
// ── La regla, y es la única que importa ──────────────────────────────────
//
//     Si un número aparece acá Y en un bloque de detalle, sale de `baseline.js`
//     o de `proyeccion.js`, importado por los dos. Nunca de una copia, nunca de
//     una fórmula equivalente.
//
// Lo que se puede escribir dos veces, algún día diverge. Por eso el patrón de la
// semana se mudó a `baseline.js` —`TusDias` lo importa de ahí— y el cierre del
// mes sale de `proyeccionMes`, que es la misma función que usa `ElMes`.
//
// De dónde sale cada renglón:
//
//   MEJOR Y PEOR DÍA   `baseline.patronSemanal` · lo dibuja `TusDias`
//   FIN DE SEMANA      propio; ningún bloque lo contesta
//   POR COCHERA        propio, en plata · los autos salen de `baseline.diaTipico`,
//                      la misma mediana que muestra `TuTecho`
//   RACHA              propio, contra la vara de `baseline.referenciaDelDia`
//   VAS A TERMINAR     `proyeccion.proyeccionMes` · lo explica `ElMes`
//   TU MEJOR DÍA       propio; el récord reciente con su fecha
//
// ── Dos cosas que NO están, y por qué ────────────────────────────────────
//
// · LA PROYECCIÓN NUNCA ES UN MONTO EXACTO. La versión vieja mostraba uno, de
//   una regla de tres, y contradecía a `ElMes` a dos bloques de distancia. Acá
//   se muestra el rango de `proyeccionMes`, que es el mismo que muestra `ElMes`
//   porque es la misma llamada.
//
// · QUÉ PARTE DE LA FACTURACIÓN SON ABONADOS. Sale de `DeDondeVieneLaPlata`,
//   cuyo período lo mueve el selector de la pestaña Ingresos: la misma función
//   con otra ventana daría dos porcentajes distintos y estaríamos en el mismo
//   pozo. Entra el día que el selector gobierne toda la pantalla.

import React, { useMemo } from "react";
import Bloque from "./Bloque";
import { ESTADO, ORIGEN, POLARIDAD, UI, ANCHO_LECTURA } from "./theme";
import { formatCurrency, fecha, entero } from "./format";
import { MIN_DIA_TIPICO, MIN_MUESTRAS, VENTANA_PATRON, diaTipico, dowDe, mediana, nombreDia, opero, patronSemanal, referenciaDelDia } from "./baseline";

// ── Constantes de método ─────────────────────────────────────────────────
const VENTANA = 30;          // de dónde salen la cochera y el mejor día reciente
const MIN_DIAS = 7;          // menos de una semana no describe nada
const MIN_RACHA = 4;         // tres días seguidos del mismo lado son casualidad

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Arriba de dos decimales el número deja de ser una frase y pasa a ser una
 *  planilla: "cada cochera recibe 0,144 autos" no lo lee nadie. */
const MAX_DECIMALES = 2;

/**
 * "4,2" — autos por cochera, o `null` si no se puede decir sin mentir.
 *
 * Este renglón es un número DERIVADO: la pantalla no dice "haces 32 autos por
 * día" —eso contradiría a `KpiCards`, que cuenta entradas sobre 30 días— sino
 * cuántos autos recibe cada cochera. El precio de derivar es que el dueño puede
 * multiplicar por sus cocheras, y entonces le tiene que dar exactamente lo que
 * publica `TuTecho` en "Un día tuyo".
 *
 * Un decimal fijo no alcanza, y el error crece con las cocheras:
 *
 *     32 autos ÷ 24 cocheras = 1,3333 → "1,3" × 24 = 31   (Tu techo: 32)
 *     32 autos ÷ 70 cocheras = 0,4571 → "0,5" × 70 = 35   (Tu techo: 32)
 *     36 autos ÷ 250 cocheras = 0,144 → "0,14" × 250 = 35 (Tu techo: 36)
 *
 * Un 9% de diferencia entre dos renglones de la misma pantalla es exactamente
 * la clase de contradicción que `baseline.js` existe para impedir, y encima en
 * el peor lugar: el resumen diciendo un número y el bloque que lo explica
 * diciendo otro.
 *
 * Así que se prueban decimales de menos a más y se usa el primero que
 * reconstruye —el caso común sigue leyéndose "4,2"— Y SI NINGUNO RECONSTRUYE,
 * SE DEVUELVE `null` Y EL RENGLÓN NO DICE NADA DE LOS AUTOS. Eso pasa en las
 * playas grandes, donde el cociente cae tan por debajo de uno que haría falta
 * un tercer decimal: ahí el dato no se puede escribir sin inventar precisión ni
 * contradecir al bloque de abajo, y las dos cosas son peores que callarse. La
 * plata por cochera —que es el titular— se sigue mostrando igual, y "cuántos
 * autos hace la playa" lo contesta `TuTecho`, que es su bloque.
 */
const autosPorCocheraTexto = (autos, cocheras) => {
  const r = autos / cocheras;
  // Arriba de diez autos por cochera se arranca sin decimales: la diferencia
  // entre 12,3 y 12,4 no decide nada.
  for (let dec = r >= 10 ? 0 : 1; dec <= MAX_DECIMALES; dec++) {
    const t = r.toFixed(dec);
    if (Math.round(Number(t) * cocheras) === autos) return t.replace(".", ",");
  }
  return null;
};


const LoQueSaltaALaVista = ({ cerrados = [], capacidad = 0 }) => {
  // El mes se pide igual que en `ElMes`: misma función, mismos argumentos. Es la
  // única forma de que los dos bloques no puedan mostrar cierres distintos.

  const datos = useMemo(() => {
    // Los días sin operar quedan afuera ANTES de armar la ventana. Si se
    // filtraran después, "los últimos 30" serían 30 casilleros con nueve días
    // cerrados adentro y todos los números saldrían de menos.
    const conMovimiento = (cerrados || []).filter((d) => d?.date && opero(d));
    if (conMovimiento.length < MIN_DIAS) return null;

    const ventana = conMovimiento.slice(-VENTANA);
    const diario = mediana(ventana.map((d) => num(d.income)));

    // ── El patrón de la semana ───────────────────────────────────────────
    // No se calcula acá: es exactamente el que dibuja `TusDias`. `suficiente`
    // decide si se puede afirmar algo, y lo decide una sola vez para los dos
    // bloques — sin eso, este resumen podría anunciar "tu mejor día es el
    // jueves" mientras el gráfico dice que todavía no hay patrón.
    const semana = patronSemanal(cerrados);

    // ── Por cochera ──────────────────────────────────────────────────────
    // Lo que deja un día tuyo normal repartido entre los lugares que tienes, y
    // cuántos autos pasa cada uno de esos lugares. Los autos salen de la misma
    // mediana que publica `TuTecho`, así que dividir a mano da lo mismo que
    // muestra aquel bloque.
    const cocheras = num(capacidad);
    const porCochera = cocheras > 0 && diario > 0 ? diario / cocheras : null;
    const tipico = diaTipico(cerrados);
    // Se guarda el número ENTERO de autos, no el cociente ya calculado: el texto
    // necesita las dos cifras para elegir los decimales con los que "autos por
    // cochera × cocheras" vuelve a dar exactamente lo que publica `TuTecho`.
    //
    // Y se calla con la MISMA cantidad de días con la que se calla `TuTecho`.
    // Sin esa condición, a un cliente de doce días este renglón le afirmaba
    // cuántos autos recibe "en un día normal" mientras el bloque de abajo le
    // decía "con 12 días de operación todavía no sé cuál es tu día normal".
    const autosDelDia =
      cocheras > 0 && tipico.dias >= MIN_DIA_TIPICO && tipico.autosMostrados > 0
        ? tipico.autosMostrados
        : null;
    // El texto se arma acá y no en el JSX porque puede salir `null`: hay playas
    // en las que el cociente no se puede escribir sin contradecir a `TuTecho`
    // (ver `autosPorCocheraTexto`), y entonces el renglón no habla de autos.
    const autosPorCochera =
      autosDelDia === null ? null : autosPorCocheraTexto(autosDelDia, cocheras);

    // ── Mejor día reciente ───────────────────────────────────────────────
    const mejor = ventana.reduce((a, b) => (num(a.income) >= num(b.income) ? a : b));

    // ── Racha ────────────────────────────────────────────────────────────
    // "Lo normal" es por día de la semana, no un promedio único: un martes
    // flojo no es un sábado flojo, y con una sola vara la racha se corta o se
    // estira según qué días cayeron en la cuenta. La vara es la MISMA que
    // muestra el gráfico de la semana, así que "por encima de lo habitual" acá
    // significa exactamente lo que ahí se ve.
    let racha = null;
    const recientes = [...conMovimiento].reverse();
    const refPrimero = referenciaDelDia(semana, recientes[0].date);
    if (refPrimero > 0) {
      const arriba = num(recientes[0].income) >= refPrimero;
      let n = 0;
      for (const d of recientes) {
        const ref = referenciaDelDia(semana, d.date);
        // Un día de la semana sin suficientes muestras no se puede juzgar, así
        // que la racha termina ahí en vez de darlo por bueno. Los días cerrados
        // ya quedaron afuera: no la cortan ni la alargan, porque no dicen nada.
        if (!(ref > 0)) break;
        if ((num(d.income) >= ref) !== arriba) break;
        n += 1;
      }
      if (n >= MIN_RACHA) racha = { n, arriba };
    }

    // ── Fin de semana contra días de semana ──────────────────────────────
    // Es el único renglón que no contesta ningún bloque de detalle, así que la
    // cuenta vive acá. La versión vieja promediaba con los días CERRADOS
    // adentro, y al que no abre los domingos le decía que la semana le rinde
    // 300% más que el fin de semana — comparaba contra un domingo de $0 que no
    // existe.
    //
    // Acá sólo entran días que operaron, y sólo se afirma si de los dos lados
    // hay muestras suficientes: al que abre de lunes a viernes no se le dice
    // nada, en vez de inventarle un fin de semana malo.
    let finde = null;
    const delFinde = [];
    const deSemana = [];
    conMovimiento.slice(-VENTANA_PATRON).forEach((d) => {
      const k = dowDe(d.date);
      (k === 0 || k === 6 ? delFinde : deSemana).push(num(d.income));
    });
    if (delFinde.length >= MIN_MUESTRAS && deSemana.length >= MIN_MUESTRAS) {
      const mf = mediana(delFinde);
      const ms = mediana(deSemana);
      if (mf > 0 && ms > 0) {
        // Se nombra al lado que gana; un porcentaje sin sujeto obliga a
        // adivinar de qué lado está el "más".
        const mejorLado = mf >= ms ? "finde" : "semana";
        const pct = Math.round((Math.max(mf, ms) / Math.min(mf, ms) - 1) * 100);
        // Menos de un 10% no es una diferencia: es ruido con dos decimales.
        if (pct >= 10) finde = { mejorLado, pct, finde: mf, semana: ms };
      }
    }

    // El rango declara la ventana MÁS ANCHA que se miró, no la más corta. Los
    // renglones de la semana leen 90 días y el de la cochera 30: poner sólo los
    // 30 diría que el mejor día se decidió con un mes de historia, que es falso.
    // Cuando el patrón está disponible es el mismo rango que declara `TusDias`.
    const rango = semana.suficiente
      ? { desde: semana.desde, hasta: semana.hasta, dias: semana.diasOperados }
      : { desde: ventana[0].date, hasta: ventana[ventana.length - 1].date, dias: ventana.length };

    return {
      semana,
      porCochera,
      cocheras,
      autosPorCochera,
      // El TOTAL de autos de un día normal. Es el mismo número que publica
      // `TuTecho` en "Un día tuyo" —sale de la misma llamada a `diaTipico`—,
      // así que decirlo acá no contradice nada. Lo que NO se puede decir es
      // "autos por cochera" en una playa grande: 0,46 autos por lugar no lo
      // lee nadie, y ése era el motivo de `autosPorCochera`.
      autosDia: tipico.autos,
      mejor: { fecha: mejor.date, monto: num(mejor.income) },
      racha,
      finde,
      rango,
    };
  }, [cerrados, capacidad]);

  // Regla de `Bloque`: si no puede generar una frase, el bloque no va. Acá el
  // estado vacío tampoco hace falta escribirlo — con menos de una semana de
  // historia, los bloques de arriba ya le explican al dueño qué le falta.
  if (!datos) return null;

  const semana = datos.semana.suficiente ? datos.semana : null;
  const diaMejorReciente = nombreDia(dowDe(datos.mejor.fecha));


  // La nota del cierre de mes dice lo mismo que el titular de `ElMes`, porque
  // sale del mismo `contraMesPasado`. Si algún día cambia el criterio, cambia en
  // los dos lados a la vez.
  // `notaMes` se borro junto con el renglon del mes: "Como termina el mes"
  // esta pegado abajo y lo dice completo, con su rango y su comparacion.
  // Repetirlo aca era la misma frase dos veces a dos centimetros.

  // El titular es la conclusión más fuerte que haya. Con las cocheras cargadas
  // es el rendimiento del lugar; si no, el mejor día de la semana; y si todavía
  // no hay patrón, el récord reciente.
  let titular;
  if (datos.porCochera !== null) {
    titular = `Cada cochera te deja ${formatCurrency(datos.porCochera)} por día`;
  } else if (semana) {
    titular = `El ${nombreDia(semana.mejor.dow)} es el día que más te deja: ${formatCurrency(semana.mejor.mediana)}`;
  } else {
    titular = `Tu mejor día reciente fue el ${diaMejorReciente} ${fecha(datos.mejor.fecha)}`;
  }

  // El titular ya dice la cochera, así que ese dato NO se repite abajo: la frase
  // se lleva su detalle y el renglón salió de la lista. Un resumen que repite su
  // propio titular dos centímetros más abajo gasta la línea más valiosa.
  const frase = datos.porCochera !== null
    ? (datos.autosDia !== null
        ? `Son ${entero(datos.autosDia)} autos repartidos entre ${entero(datos.cocheras)} lugares, para ponerlo al lado de lo que te cuesta mantenerlos.`
        : `Sobre ${entero(datos.cocheras)} cocheras cargadas, para ponerlo al lado de lo que te cuesta mantenerlas.`)
    : "Cada línea es una conclusión sola, y todas se explican más abajo en su propio bloque.";

  // Sólo se muestra el día flojo si de verdad deja plata: un "$0" acá sería un
  // día que la playa abrió y no cobró nada, y eso lo reporta `Tus días` como una
  // anomalía, no como el ritmo normal de ese día.
  const hayFlojo = semana && semana.peor && semana.peor.mediana > 0 && semana.peor.dow !== semana.mejor.dow;

  // Cada renglón es una oración cerrada con su número adentro: así se lee de
  // corrido y se barre de un vistazo, que es para lo que existe este bloque.
  const renglones = [];

  if (semana) {
    renglones.push({
      id: "mejor-dow",
      icono: "emoji_events",
      color: POLARIDAD.entra.color,
      texto: `Tu mejor día de la semana es el ${nombreDia(semana.mejor.dow)}: te deja ${formatCurrency(semana.mejor.mediana)}, y es el que conviene tener cubierto con todo el personal.`,
    });
  }

  if (hayFlojo) {
    renglones.push({
      id: "peor-dow",
      icono: "trending_down",
      color: UI.textoSuave,
      texto: `El ${nombreDia(semana.peor.dow)} es el más flojo de los días que abres, con ${formatCurrency(semana.peor.mediana)}: es el mejor candidato para una promoción, porque es el día en el que te sobra lugar.`,
    });
  }

  if (datos.finde) {
    renglones.push({
      id: "finde",
      icono: "weekend",
      color: ORIGEN.tickets.color,
      texto: datos.finde.mejorLado === "finde"
        ? `Tus fines de semana te dejan ${datos.finde.pct}% más que un día de semana: ${formatCurrency(datos.finde.finde)} contra ${formatCurrency(datos.finde.semana)} en un día típico.`
        : `Tus días de semana te dejan ${datos.finde.pct}% más que un día de fin de semana: ${formatCurrency(datos.finde.semana)} contra ${formatCurrency(datos.finde.finde)} en un día típico.`,
    });
  }


  if (datos.racha) {
    renglones.push({
      id: "racha",
      icono: datos.racha.arriba ? "local_fire_department" : "warning",
      color: datos.racha.arriba ? POLARIDAD.entra.color : POLARIDAD.sale.color,
      texto: datos.racha.arriba
        ? `Llevas ${datos.racha.n} días seguidos por encima de lo habitual para cada uno de esos días: si sabes qué cambió, conviene anotarlo.`
        : `Llevas ${datos.racha.n} días seguidos por debajo de lo habitual para cada uno de esos días: vale la pena mirar qué cambió.`,
    });
  }


  renglones.push({
    id: "mejor-dia",
    icono: "star",
    color: ESTADO.anulado.color,
    texto: `Tu mejor día reciente fue el ${diaMejorReciente} ${fecha(datos.mejor.fecha)}, con ${formatCurrency(datos.mejor.monto)}: si sabes qué pasó, es lo único que después se puede repetir a propósito.`,
  });

  return (
    <Bloque
      nombre="Lo que salta a la vista"
      id="lo-que-salta-a-la-vista"
      titular={titular}
      frase={frase}
      // Sin acento de color a propósito: son números de temas distintos y una
      // barra de color les inventaría un significado común que no tienen.
      rango={`${fecha(datos.rango.desde)} – ${fecha(datos.rango.hasta)} · ${datos.rango.dias} días con movimiento`}
    >
      {/* UNA FRASE POR RENGLÓN, con su ícono. No una tabla de tres columnas.
          La primera versión partía cada dato en rótulo / monto / explicación y
          obligaba a rearmar la frase con la vista. Diego lo comparó contra la
          pantalla vieja: "sigo pensando que la versión anterior sobre ese tema
          es más visual, más entendible". Tenía razón — el valor de este bloque
          es que se BARRE de un vistazo, y para eso cada renglón tiene que ser
          una oración cerrada, no tres pedazos.
          El orden es de más a menos accionable: primero lo que se decide esta
          semana, después el rendimiento del lugar, y al final lo que ya pasó. */}
      <div className="d-flex flex-column gap-2" style={{ marginTop: 2, maxWidth: ANCHO_LECTURA }}>
        {renglones.map((r) => (
          <div key={r.id} className="d-flex align-items-start gap-2">
            <span
              className="material-symbols-rounded"
              style={{ fontSize: 18, marginTop: 1, color: r.color, flexShrink: 0 }}
            >
              {r.icono}
            </span>
            <span style={{ fontSize: 13, color: UI.texto, lineHeight: 1.45 }}>{r.texto}</span>
          </div>
        ))}
      </div>
    </Bloque>
  );
};

export default LoQueSaltaALaVista;
